// src/components/Overtime/BulkApproveWithRateModal.jsx
import React, { useState } from "react";
import { Modal, Button, Form, Table, Badge } from "react-bootstrap";
import moment from "moment";
import { API_URL } from "../..";

const BulkApproveWithRateModal = ({ records = [], onClose, onSuccess }) => {
  const [config, setConfig] = useState({}); // { overtimeId: { type: "hourly"|"fixed", value: number } }
  const [loading, setLoading] = useState(false);

  // Process each overtime record individually
  const overtimeRows = records.map((r) => {
    const start = moment(r.start_time, "HH:mm:ss");
    const end = moment(r.end_time, "HH:mm:ss");
    let diff = end.diff(start, "minutes");
    if (diff < 0) diff += 24 * 60;
    const hours = parseFloat((diff / 60)?.toFixed(2));

    return {
      id: r.id,
      userId: r.user?.id,
      userName: r.user?.name || "Unknown",
      date: moment(r.date).format("DD MMM YYYY"),
      hours,
      startTime: start.format("hh:mm A"),
      endTime: end.format("hh:mm A"),
    };
  });

  const handleTypeChange = (id, isFixed) => {
    setConfig((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        type: isFixed ? "fixed" : "hourly",
      },
    }));
  };

  const handleValueChange = (id, value) => {
    const num = value ? parseFloat(value) || 0 : 0;
    setConfig((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        value: num,
      },
    }));
  };

  const getAmount = (row) => {
    const conf = config[row.id] || { type: "hourly", value: 0 };
    return conf.type === "fixed" ? conf.value : conf.value * row.hours;
  };

  const grandTotal = overtimeRows.reduce((sum, row) => sum + getAmount(row), 0);

  const handleBulkApprove = async () => {
    const invalid = overtimeRows.some((row) => {
      const c = config[row.id];
      return !c || !c.value || c.value <= 0;
    });

    if (invalid) {
      alert("Please set a valid rate/amount for every overtime request.");
      return;
    }

    setLoading(true);
    const loggedUser = JSON.parse(localStorage.getItem("user"));

    try {
      const payload = {
        ids: overtimeRows.map((r) => r.id),
        status: "approved",
        approved_id: loggedUser.id,
        overtime_config: overtimeRows.map((row) => {
          const c = config[row.id];
          return {
            overtime_id: row.id,
            type: c.type,
            value: c.value,
          };
        }),
      };

      const res = await fetch(`${API_URL}/api/overtime/bulk/approve-with-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (json.success) {
        alert(`Approved ${overtimeRows.length} overtime request(s) successfully!`);
        onSuccess();
        onClose();
      } else {
        alert(json.message || "Failed to approve");
      }
    } catch (err) {
      console.error(err);
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Bulk Approve Overtime — Set Rate per Request ({overtimeRows.length})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          Set <strong>Fixed Amount</strong> or <strong>Hourly Rate</strong> for each overtime request individually.
        </p>

        <Table bordered hover responsive striped size="sm">
          <thead className="table-light">
            <tr>
              <th>Employee</th>
              <th>Date</th>
              <th>Time</th>
              <th>Hours</th>
              <th style={{ width: "90px" }}>
                <Form.Check type="checkbox" label="Fixed" disabled className="fw-bold" />
              </th>
              <th>Rate / Amount (£)</th>
              <th>Total (£)</th>
            </tr>
          </thead>
          <tbody>
            {overtimeRows.map((row) => {
              const conf = config[row.id] || { type: "hourly", value: "" };
              const amount = getAmount(row);

              return (
                <tr key={row.id}>
                  <td>
                    <strong>{row.userName}</strong>
                  </td>
                  <td>{row.date}</td>
                  <td>
                    <small>
                      {row.startTime} – {row.endTime}
                    </small>
                  </td>
                  <td className="text-center text-primary fw-bold">
                    {row.hours?.toFixed(2)}h
                  </td>
                  <td className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={conf.type === "fixed"}
                      onChange={(e) => handleTypeChange(row.id, e.target.checked)}
                      disabled={loading}
                    />
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={conf.value}
                        onChange={(e) => handleValueChange(row.id, e.target.value)}
                        placeholder={conf.type === " Phuket fixed" ? "Fixed £" : "Rate/h"}
                        style={{ width: "120px" }}
                        disabled={loading}
                      />
                      <small className="text-muted nowrap">
                        {conf.type === "fixed" ? "Fixed" : "/hour"}
                      </small>
                    </div>
                  </td>
                  <td className="text-end fw-bold text-success">
                    £{amount?.toFixed(2)}
                  </td>
                </tr>
              );
            })}
            <tr className="table-success fw-bold">
              <td colSpan="6" className="text-end">
                Grand Total:
              </td>
              <td className="text-end fs-5">
                £{grandTotal?.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleBulkApprove}
          disabled={loading || overtimeRows.length === 0}
        >
          {loading ? "Approving..." : `Approve All (${overtimeRows.length})`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkApproveWithRateModal;